import { TierBadge } from '../ui/bits';
import { ordinalSuffix, signedPoints } from '../../lib/format';
import { zoneForRank, zoneLabel } from '../../lib/round';
import { cn } from '../../lib/cn';

/**
 * The live hero on the picks-tracker screen. While matches are in play the big
 * number is the PROJECTED round score (points already banked + points the in-play
 * fixtures would bank if they ended now), with the split spelled out below. The
 * group rank stays tied to settled results (it only moves as fixtures finish), so
 * it's labelled accordingly to stay honest.
 */
export function LiveProvisionalHero({
  banked,
  inPlay,
  live,
  rankInGroup,
  divisionSize,
  fixturesPlayed,
  fixturesLive,
  picksRequired,
  tierIndex
}: {
  banked: number;
  inPlay: number;
  live: boolean;
  rankInGroup: number | null;
  divisionSize: number | null;
  fixturesPlayed: number;
  fixturesLive: number;
  picksRequired: number;
  tierIndex: number;
}) {
  const zone = rankInGroup && divisionSize ? zoneForRank(rankInGroup, divisionSize) : null;
  const projected = banked + inPlay;

  return (
    <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#23232C] to-[#0C0C10] p-[22px] shadow-[0_22px_42px_-18px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.08]">
      <span className="pointer-events-none absolute -right-[50px] -top-[60px] h-[200px] w-[200px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.22),transparent_70%)]" />

      <div className="relative">
        <div className="flex items-center gap-[7px]">
          {live && <span className="h-[7px] w-[7px] animate-h2h-pulse rounded-full bg-accent" />}
          <span className="text-[11px] font-bold uppercase tracking-wide text-[#9A9AA4]">
            {live ? 'Projected points' : 'Points so far'}
          </span>
        </div>

        <div className="mt-3 flex items-baseline gap-[10px]">
          <span className="num text-[54px] font-extrabold leading-none tracking-title text-white">
            {signedPoints(live ? projected : banked)}
          </span>
        </div>

        {live && (
          <div className="mt-2 text-[12px] font-semibold text-[#9A9AA4]">
            <span className="text-white/85">{signedPoints(banked)}</span> banked ·{' '}
            <span className="text-accent">{signedPoints(inPlay)}</span> in play
          </div>
        )}

        {rankInGroup && divisionSize ? (
          <div className="mt-[18px] flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.06] p-[13px_15px]">
            <TierBadge tierIndex={tierIndex} size={38} />
            <div className="flex-1">
              <div className="text-[14px] font-bold tracking-snug text-white">
                Currently {rankInGroup}
                {ordinalSuffix(rankInGroup)} of {divisionSize}
              </div>
              <div className="mt-[1px] text-[11px] font-medium text-[#9A9AA4]">
                {live ? 'Rank updates as fixtures finish' : zone ? zoneLabel(zone) : 'Live group standing'}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-[18px] rounded-2xl border border-white/[0.08] bg-white/[0.06] p-[13px_15px] text-[12px] font-medium text-[#9A9AA4]">
            Your group board appears once the round&apos;s first results land.
          </div>
        )}

        <div className="mt-[14px] flex items-center gap-[10px]">
          <div className="flex flex-1 gap-[5px]">
            {Array.from({ length: picksRequired }, (_, i) => {
              const played = i < fixturesPlayed;
              const inPlayPip = !played && i < fixturesPlayed + fixturesLive;
              return (
                <div
                  key={i}
                  className={cn(
                    'h-[5px] flex-1 rounded-[3px]',
                    played
                      ? 'bg-accent'
                      : inPlayPip
                        ? 'animate-h2h-pulse bg-accent/50'
                        : 'bg-white/[0.14]'
                  )}
                />
              );
            })}
          </div>
          <span className="num whitespace-nowrap text-[11px] font-semibold text-[#9A9AA4]">
            {fixturesLive > 0 ? `${fixturesLive} live` : `${fixturesPlayed}/${picksRequired} cards settled`}
          </span>
        </div>
      </div>
    </div>
  );
}
